function getExt(name = "") {
    const i = name.lastIndexOf(".");
    return i >= 0 ? name.slice(i + 1).toLowerCase() : "";
}

export const MAX_IMAGE_DIMENSION = 6500;

export function validateGpxFile(file) {
    if (!file || getExt(file.name) !== "gpx") {
        return { ok: false, error: "Выберите файл трека в формате GPX (.gpx)." };
    }
    if (!file.size) {
        return { ok: false, error: "GPX-файл пуст. Выберите файл с данными трека." };
    }
    if (file.size > 30 * 1024 * 1024) {
        return { ok: false, error: "Размер одного GPX-файла не должен превышать 30 МБ." };
    }
    return { ok: true };
}

async function validateGpxContent(file) {
    let text;
    try {
        text = typeof file.text === "function" ? await file.text() : await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reader.onabort = () => reject(new Error("GPX read failed"));
            reader.readAsText(file, "UTF-8");
        });
        if (/<!DOCTYPE|<!ENTITY/i.test(text)) throw new Error("Unsupported XML declaration");
        const xml = new DOMParser().parseFromString(text, "application/xml");
        if (xml.getElementsByTagName("parsererror").length || !xml.documentElement) throw new Error("Invalid XML");
        const namespace = "http://www.topografix.com/GPX/1/1";
        if (xml.documentElement.localName !== "gpx" || xml.documentElement.namespaceURI !== namespace) {
            return { ok: false, error: "Для импорта нужен GPX версии 1.1." };
        }
        const points = xml.getElementsByTagNameNS(namespace, "trkpt");
        for (const point of points) {
            const coordinate = (name) => {
                const value = point.getAttribute(name);
                return value?.trim() ? Number(value) : NaN;
            };
            const lat = coordinate("lat"), lon = coordinate("lon");
            if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) {
                return { ok: false, error: "В GPX есть некорректные координаты." };
            }
        }
        if (points.length < 2) return { ok: false, error: "В GPX нет трека. Экспортируйте маршрут как GPX-трек." };
        return { ok: true };
    } catch {
        return { ok: false, error: "Не удалось прочитать GPX. Проверьте файл и экспортируйте его заново." };
    }
}

export async function validateUploadFile(file, validationType = "image", maxDimension = MAX_IMAGE_DIMENSION) {
    if (validationType === "gpx") {
        const validation = validateGpxFile(file);
        return validation.ok ? validateGpxContent(file) : validation;
    }
    // Mixed document uploads have their own format and size checks in the form.
    if (validationType === "file" && !file?.type?.startsWith("image/")
        && !["jpg", "jpeg", "png", "webp"].includes(getExt(file?.name))) {
        return { ok: true };
    }
    return validateImageDimensions(file, maxDimension);
}

export async function inspectImageDimensions(file) {
    if (!(file instanceof Blob)) {
        throw new Error("Файл изображения не выбран.");
    }

    if (typeof createImageBitmap === "function") {
        const bitmap = await createImageBitmap(file);
        try {
            return { width: bitmap.width, height: bitmap.height };
        } finally {
            bitmap.close?.();
        }
    }

    const objectUrl = URL.createObjectURL(file);
    try {
        return await new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve({
                width: image.naturalWidth || image.width,
                height: image.naturalHeight || image.height,
            });
            image.onerror = () => reject(new Error("Не удалось прочитать изображение."));
            image.src = objectUrl;
        });
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

export async function validateImageDimensions(file, maxDimension = MAX_IMAGE_DIMENSION) {
    if (file?.size > 25 * 1024 * 1024) {
        return { ok: false, error: "Файл слишком большой. Максимальный размер — 25 МБ." };
    }
    try {
        const { width, height } = await inspectImageDimensions(file);
        if (width > maxDimension || height > maxDimension) {
            return {
                ok: false,
                width,
                height,
                error: `Разрешение изображения ${width}×${height} px. Максимально допустимо ${maxDimension}×${maxDimension} px.`,
            };
        }
        return { ok: true, width, height };
    } catch {
        return { ok: false, error: "Не удалось прочитать изображение. Выберите исправный файл JPG, PNG или WEBP." };
    }
}

export function createImageValidator({
        maxBytes = 25 * 1024 * 1024, // 25MB
        allowedMime = ["image/jpeg", "image/png", "image/webp"],
        allowedExt = ["jpg", "jpeg", "png", "webp"],
    } = {}) {
    const ALLOWED_MIME = new Set(allowedMime);
    const ALLOWED_EXT = new Set(allowedExt);

    return function validateImageFile(file) {
        if (!file) return { ok: false, error: "Файл не выбран." };

        // размер
        if (typeof file.size === "number" && file.size > maxBytes) {
        const mb = (file.size / 1024 / 1024).toFixed(1);
        const limitMb = (maxBytes / 1024 / 1024).toFixed(0);
        return { ok: false, error: `Файл слишком большой: ${mb}MB (лимит ${limitMb}MB).` };
        }

        // mime (основная проверка)
        const hasMime = typeof file.type === "string" && file.type.length > 0;
        if (hasMime && !ALLOWED_MIME.has(file.type)) {
        return { ok: false, error: `Недопустимый формат: ${file.type}.` };
        }

        // расширение (фоллбек)
        const ext = getExt(file.name);
        if (!ALLOWED_EXT.has(ext)) {
        return { ok: false, error: `Недопустимое расширение файла: .${ext || "?"}.` };
        }

        return { ok: true };
    };
}
